import React, { useEffect, useRef } from "react";

// Orchestration map for AgentRunDemo: request -> coordinator -> specialist agents -> business systems,
// with packets travelling along whichever connections are active in the current phase.
const W = 760;
const ROW = 66;
const REQ = { x: 18, w: 112, h: 46 };
const COORD = { x: 258, r: 30 };
const SPEC = { x: 390, w: 178, h: 44 };
const SYS = { x: 610, w: 136, h: 34 };

const shorten = (text, max) => (text.length > max ? `${text.slice(0, max - 1).trim()}…` : text);
const prefersReducedMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

function edgeState(phase, from, finalPhase) {
  if (phase >= finalPhase) return "done";
  if (phase === from) return "active";
  return phase > from ? "done" : "idle";
}

function AgentFlowMap({ agents, systems, phase, finalPhase }) {
  const svgRef = useRef(null);
  const n = agents.length;
  const H = Math.max(220, n * ROW + 44);
  const cy = H / 2;
  const rowY = (i) => cy + (i - (n - 1) / 2) * ROW;
  const done = phase >= finalPhase;

  // Packets are positioned along their path each frame; which ones exist depends on the phase.
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg || prefersReducedMotion()) return undefined;
    let frame;
    const start = performance.now();
    const tick = (now) => {
      svg.querySelectorAll("[data-edge]").forEach((dot) => {
        const path = svg.querySelector(`#${dot.dataset.edge}`);
        if (!path) return;
        const length = path.getTotalLength();
        const t = (((now - start) / Number(dot.dataset.dur)) + Number(dot.dataset.offset || 0)) % 1;
        const point = path.getPointAtLength((dot.dataset.reverse ? 1 - t : t) * length);
        dot.setAttribute("cx", point.x);
        dot.setAttribute("cy", point.y);
        dot.style.opacity = String(Math.min(1, Math.sin(Math.PI * t) * 2.2));
      });
      frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [phase, n]);

  const packets = [];
  if (phase <= 1) packets.push({ edge: "flow-req", dur: 1100 });
  if (phase === 1) agents.forEach((_, i) => packets.push({ edge: `flow-spec-${i}`, dur: 1300, offset: i * 0.2 }));
  if (phase >= 2 && phase < finalPhase) {
    const i = phase - 2;
    packets.push({ edge: `flow-spec-${i}`, dur: 1200 }, { edge: `flow-sys-${i}`, dur: 700 }, { edge: `flow-sys-${i}`, dur: 700, reverse: true, offset: 0.5 });
  }
  if (done) {
    agents.forEach((_, i) => packets.push({ edge: `flow-spec-${i}`, dur: 1500, reverse: true, offset: i * 0.25 }));
    packets.push({ edge: "flow-req", dur: 1500, reverse: true });
  }

  return (
    <div className={`agent-flow${done ? " is-complete" : ""}`} aria-hidden="true">
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet">
        <defs>
          <linearGradient id="flow-grad" x1="0" x2="1"><stop offset="0" stopColor="#762681" /><stop offset="1" style={{ stopColor: "var(--case-accent, #bf3a96)" }} /></linearGradient>
          <radialGradient id="flow-glow"><stop offset="0" style={{ stopColor: "var(--case-accent, #bf3a96)", stopOpacity: 0.35 }} /><stop offset="1" style={{ stopColor: "var(--case-accent, #bf3a96)", stopOpacity: 0 }} /></radialGradient>
        </defs>

        <path id="flow-req" className={`flow-edge is-${phase <= 1 ? "active" : "done"}`} d={`M ${REQ.x + REQ.w} ${cy} L ${COORD.x - COORD.r} ${cy}`} />
        {agents.map((agent, i) => {
          const y = rowY(i);
          return (
            <g key={agent.name}>
              <path id={`flow-spec-${i}`} className={`flow-edge is-${phase === 1 ? "active" : edgeState(phase, i + 2, finalPhase)}`}
                d={`M ${COORD.x + COORD.r} ${cy} C ${COORD.x + 80} ${cy}, ${SPEC.x - 60} ${y}, ${SPEC.x} ${y}`} />
              <path id={`flow-sys-${i}`} className={`flow-edge is-${edgeState(phase, i + 2, finalPhase)}`} d={`M ${SPEC.x + SPEC.w} ${y} L ${SYS.x} ${y}`} />
            </g>
          );
        })}

        <g className={`flow-node flow-request${done ? " is-done" : " is-on"}`}>
          <rect x={REQ.x} y={cy - REQ.h / 2} width={REQ.w} height={REQ.h} rx="12" />
          <text x={REQ.x + REQ.w / 2} y={cy - 3}>{done ? "Result ready" : "Request"}</text>
          <text className="flow-sub" x={REQ.x + REQ.w / 2} y={cy + 12}>{done ? "for your review" : "from your team"}</text>
        </g>

        <g className={`flow-node flow-coordinator${phase === 1 || done ? " is-working" : ""}`}>
          <circle className="flow-halo" cx={COORD.x} cy={cy} r={COORD.r + 22} fill="url(#flow-glow)" />
          <circle className="flow-ring" cx={COORD.x} cy={cy} r={COORD.r + 7} />
          <circle cx={COORD.x} cy={cy} r={COORD.r} fill="url(#flow-grad)" />
          <text className="flow-mark" x={COORD.x} y={cy + 6}>✦</text>
          <text className="flow-caption" x={COORD.x} y={cy + COORD.r + 26}>Coordinator</text>
        </g>

        {agents.map((agent, i) => {
          const y = rowY(i);
          const state = done ? "done" : edgeState(phase, i + 2, finalPhase) === "active" ? "working" : phase > i + 2 ? "done" : "queued";
          const system = systems[i % systems.length];
          return (
            <g key={agent.name}>
              <g className={`flow-node flow-agent is-${state}`}>
                <rect x={SPEC.x} y={y - SPEC.h / 2} width={SPEC.w} height={SPEC.h} rx="11" />
                <circle className="flow-agent-dot" cx={SPEC.x + 20} cy={y} r="9" />
                <text className="flow-agent-icon" x={SPEC.x + 20} y={y + 3.5}>{state === "done" ? "✓" : String(i + 1).padStart(2, "0")}</text>
                <text className="flow-agent-name" x={SPEC.x + 38} y={y + 4}>{shorten(agent.name, 22)}</text>
                {state === "working" && <rect className="flow-scan" x={SPEC.x} y={y - SPEC.h / 2} width={SPEC.w} height={SPEC.h} rx="11" />}
              </g>
              <g className={`flow-node flow-system is-${state}`}>
                <rect x={SYS.x} y={y - SYS.h / 2} width={SYS.w} height={SYS.h} rx="8" />
                <text x={SYS.x + SYS.w / 2} y={y + 4}>{shorten(system, 19)}</text>
              </g>
            </g>
          );
        })}

        {packets.map((packet, index) => (
          <circle key={`${phase}-${index}`} className="flow-packet" r="4.5" cx="-20" cy="-20"
            data-edge={packet.edge} data-dur={packet.dur} data-offset={packet.offset || 0} data-reverse={packet.reverse ? "1" : undefined} />
        ))}
      </svg>
      <div className="agent-flow-legend"><span><i className="is-coord" />Coordinator</span><span><i className="is-agent" />Specialist agents</span><span><i className="is-system" />Your systems</span></div>
    </div>
  );
}

export default AgentFlowMap;
